import React, { useEffect, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { CheckCircle2, AlertCircle, Mail } from 'lucide-react';
import { Button } from '../components/ui/button';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SeoHead from '../components/SeoHead';
import { useLanguage } from '../hooks/useLanguage';

const VALID_STATUSES = new Set([
  'pending',
  'confirmed',
  'already_verified',
  'invalid',
  'error',
]);

const NewsletterConfirmationPage = () => {
  const { t } = useLanguage();
  const [searchParams] = useSearchParams();
  const rawStatus = searchParams.get('status') || 'confirmed';
  const status = VALID_STATUSES.has(rawStatus) ? rawStatus : 'invalid';

  const content = useMemo(() => {
    const byStatus = {
      pending: {
        icon: Mail,
        iconClass: 'bg-primary-100 text-primary-600',
        title: t('newsletterConfirmation.titlePending'),
        subtitle: t('newsletterConfirmation.subtitlePending'),
      },
      confirmed: {
        icon: CheckCircle2,
        iconClass: 'bg-green-100 text-green-600',
        title: t('newsletterConfirmation.titleConfirmed'),
        subtitle: t('newsletterConfirmation.subtitleConfirmed'),
      },
      already_verified: {
        icon: CheckCircle2,
        iconClass: 'bg-blue-100 text-blue-600',
        title: t('newsletterConfirmation.titleAlreadyVerified'),
        subtitle: t('newsletterConfirmation.subtitleAlreadyVerified'),
      },
      invalid: {
        icon: AlertCircle,
        iconClass: 'bg-amber-100 text-amber-600',
        title: t('newsletterConfirmation.titleInvalid'),
        subtitle: t('newsletterConfirmation.subtitleInvalid'),
      },
      error: {
        icon: AlertCircle,
        iconClass: 'bg-red-100 text-red-600',
        title: t('newsletterConfirmation.titleError'),
        subtitle: t('newsletterConfirmation.subtitleError'),
      },
    };
    return byStatus[status];
  }, [status, t]);

  const Icon = content.icon;

  useEffect(() => {
    document.body.classList.add('header-dark-wrapper');
    const style = document.createElement('style');
    style.innerHTML = `
      .header-dark-wrapper header {
        background-color: #0B1F3F !important;
      }
      .header-dark-wrapper header nav a,
      .header-dark-wrapper header > div > div > div > button {
        color: white !important;
      }
    `;
    document.head.appendChild(style);
    return () => {
      document.body.classList.remove('header-dark-wrapper');
      style.remove();
    };
  }, []);

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <SeoHead
        title={`${content.title} | Wownautic`}
        description={content.subtitle}
        canonicalPath="/newsletter-confirmation"
        noindex
      />
      <Header />

      <main className="flex-1 pt-28 pb-16">
        <div className="max-w-lg mx-auto px-4 text-center">
          <div
            className={`inline-flex items-center justify-center w-20 h-20 rounded-full mb-5 ${content.iconClass}`}
          >
            <Icon className="w-11 h-11" />
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-primary-950 mb-3">{content.title}</h1>
          <p className="text-lg text-gray-600 mb-8">{content.subtitle}</p>

          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button asChild size="lg" className="bg-primary-600 hover:bg-primary-700">
              <Link to="/">{t('newsletterConfirmation.backHome')}</Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="border-primary-300">
              <Link to="/#offers">{t('newsletterConfirmation.browseOffers')}</Link>
            </Button>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default NewsletterConfirmationPage;
